import {
  closestCorners,
  getFirstCollision,
  KeyboardCode,
  KeyboardSensor as LibKeyboardSensor,
  MouseSensor as LibMouseSensor,
  PointerSensor as LibPointerSensor,
} from '@dnd-kit/core';
import type { DraggableNode, KeyboardCoordinateGetter, KeyboardSensorOptions } from '@dnd-kit/core';

const directions = [KeyboardCode.Down, KeyboardCode.Right, KeyboardCode.Up, KeyboardCode.Left];

const originalKeyboardActivators = LibKeyboardSensor.activators;
const originalHandler = originalKeyboardActivators[0].handler;

function hasNativeEventTarget(x: unknown): x is { nativeEvent: { target?: EventTarget | null } } {
  return x !== null && typeof x === 'object' && 'nativeEvent' in x;
}

function getEventTarget(event: unknown): EventTarget | null {
  if (!hasNativeEventTarget(event)) return null;
  return event.nativeEvent?.target ?? null;
}

function getIsInteractiveElement(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
      target.getAttribute('contenteditable') === 'true')
  );
}

/**
 * Keyboard sensor that ignores drag-related keys when the event originates from an
 * INPUT, TEXTAREA, SELECT, or contenteditable. Keeps typing natural while the card
 * remains focusable and draggable via keyboard elsewhere.
 */
export class SelectiveKeyboardSensor extends LibKeyboardSensor {
  static activators = [
    {
      eventName: 'onKeyDown' as const,
      handler: (
        event: Event,
        options: KeyboardSensorOptions,
        context: { active: DraggableNode }
      ) => {
        if (getIsInteractiveElement(getEventTarget(event))) return false;
        return originalHandler(event, options, context);
      },
    },
  ];
}

/**
 * Mouse sensor that ignores drag when the mousedown originates from an interactive
 * element (INPUT, TEXTAREA, SELECT, contenteditable). Allows text selection inside
 * inputs on draggable cards.
 */
export class SelectiveMouseSensor extends LibMouseSensor {
  static activators = [
    {
      eventName: 'onMouseDown' as const,
      handler: (...args: Parameters<(typeof LibMouseSensor.activators)[0]['handler']>) => {
        if (getIsInteractiveElement(getEventTarget(args[0]))) return false;
        return LibMouseSensor.activators[0].handler(...args);
      },
    },
  ];
}

/**
 * Pointer sensor that ignores drag when the pointerdown originates from an interactive
 * element (INPUT, TEXTAREA, SELECT, contenteditable). Allows text selection inside
 * inputs on draggable cards.
 */
export class SelectivePointerSensor extends LibPointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown' as const,
      handler: (...args: Parameters<(typeof LibPointerSensor.activators)[0]['handler']>) => {
        if (getIsInteractiveElement(getEventTarget(args[0]))) return false;
        return LibPointerSensor.activators[0].handler(...args);
      },
    },
  ];
}

/**
 * Custom keyboard coordinate getter for 2D kanban board layouts.
 * Handles directional navigation (up/down within columns, left/right between columns).
 */
export const kanbanKeyboardCoordinateGetter: KeyboardCoordinateGetter = (event, { context }) => {
  const { active, collisionRect, droppableContainers, droppableRects } = context;

  if (!directions.includes(event.code as KeyboardCode)) return;
  if (!active || !collisionRect) return;

  event.preventDefault();

  const filteredContainers = [];
  for (const entry of droppableContainers.getEnabled()) {
    const rect = droppableRects.get(entry.id);
    if (!rect) continue;

    switch (event.code) {
      case KeyboardCode.Down:
        if (collisionRect.top < rect.top) filteredContainers.push(entry);
        break;
      case KeyboardCode.Up:
        if (collisionRect.top > rect.top) filteredContainers.push(entry);
        break;
      case KeyboardCode.Left:
        if (collisionRect.left >= rect.left + rect.width) filteredContainers.push(entry);
        break;
      case KeyboardCode.Right:
        if (collisionRect.left + collisionRect.width <= rect.left) filteredContainers.push(entry);
        break;
    }
  }

  const collisions = closestCorners({
    active,
    collisionRect,
    droppableContainers: filteredContainers,
    droppableRects,
    pointerCoordinates: null,
  });

  const closestId = getFirstCollision(collisions, 'id');
  if (closestId != null) {
    const newDroppable = droppableContainers.get(closestId);
    const newRect = newDroppable?.rect.current;
    if (newRect) return { x: newRect.left, y: newRect.top };
  }

  return undefined;
};
